/**
 * BranchManagementPanel — SlidePanel tab for branch CRUD, diff, merge,
 * and approval workflow.
 *
 * Layout:
 *   1. Branch list with metadata (author, fork point, commit count)
 *   2. Diff viewer between selected branch and main
 *   3. Merge trigger with approval status
 */

import { useCallback, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Callout,
  Flex,
  Heading,
  ScrollArea,
  Text,
} from "@radix-ui/themes";

import { useProvenanceContext } from "../../context/ProvenanceContext";
import {
  useBranches,
  useCommits,
  useDeleteBranch,
  useDiffBranches,
} from "../../hooks/useVersioning";
import { useCurrentRole, useCurrentUserId } from "../../hooks/useCurrentRole";
import { NewBranchDialog } from "./NewBranchDialog";
import { MergeDialog } from "./MergeDialog";
import { RenameBranchDialog } from "./RenameBranchDialog";
import { TagDialog } from "./TagDialog";
import { useSquashCommits } from "../../hooks/useVersioning";
import type { Branch } from "../../api/versioning";

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function BranchCard({
  branch,
  projectId,
  isActive,
  canMerge,
  onSelect,
  onSwitch,
  onDelete,
  onMerge,
  onRename,
  onSquash,
}: {
  branch: Branch;
  projectId: string;
  isActive: boolean;
  canMerge: boolean;
  onSelect: () => void;
  onSwitch: () => void;
  onDelete: () => void;
  onMerge: () => void;
  onRename: () => void;
  onSquash?: () => void;
}) {
  const isMain = branch.name === "main";
  const { data: commits } = useCommits(projectId, branch.name);
  const commitCount = commits?.length ?? 0;

  return (
    <Box
      p="3"
      onClick={onSelect}
      style={{
        borderRadius: "var(--radius-2)",
        border: isActive
          ? "2px solid var(--accent-9)"
          : "1px solid var(--gray-a4)",
        background: isActive ? "var(--accent-a2)" : "var(--color-surface)",
        cursor: "pointer",
        transition: "border-color 150ms, background 150ms",
      }}
    >
      <Flex align="center" justify="between" mb="1">
        <Flex align="center" gap="2">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
            <path d="M9.5 3.25a2.25 2.25 0 1 1 3 2.122V6A2.5 2.5 0 0 1 10 8.5H6a1 1 0 0 0-1 1v1.128a2.251 2.251 0 1 1-1.5 0V5.372a2.25 2.25 0 1 1 1.5 0v1.836A2.5 2.5 0 0 1 6 7h4a1 1 0 0 0 1-1v-.628A2.25 2.25 0 0 1 9.5 3.25Zm-6 0a.75.75 0 1 0 1.5 0 .75.75 0 0 0-1.5 0Zm8.25-.75a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM4.25 12a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5Z" />
          </svg>
          <Text size="2" weight="medium">
            {branch.name}
          </Text>
          {isMain && (
            <Badge size="1" color="blue">
              default
            </Badge>
          )}
        </Flex>
        <Text size="1" color="gray">
          {commitCount} commit{commitCount !== 1 ? "s" : ""}
        </Text>
      </Flex>

      <Flex align="center" gap="3" mb="2">
        <Text size="1" color="gray">
          by {branch.created_by}
        </Text>
        <Text size="1" color="gray" title={formatDate(branch.created_at)}>
          {relativeTime(branch.created_at)}
        </Text>
        {branch.parent_branch && !isMain && (
          <Text size="1" color="gray">
            from <Text weight="medium">{branch.parent_branch}</Text>
          </Text>
        )}
      </Flex>

      {isActive && (
        <Flex gap="2" mt="1">
          <Button size="1" variant="soft" onClick={(e) => { e.stopPropagation(); onSwitch(); }}>
            Switch to
          </Button>
          {!isMain && (
            <>
              <Button
                size="1"
                variant="soft"
                onClick={(e) => { e.stopPropagation(); onRename(); }}
              >
                Rename
              </Button>
              {commitCount > 1 && onSquash && (
                <Button
                  size="1"
                  variant="soft"
                  color="violet"
                  onClick={(e) => { e.stopPropagation(); onSquash(); }}
                >
                  Squash
                </Button>
              )}
              <Button
                size="1"
                variant="soft"
                color="green"
                disabled={!canMerge}
                title={canMerge ? undefined : "Only project owners can merge branches"}
                onClick={(e) => { e.stopPropagation(); onMerge(); }}
              >
                {canMerge ? "Merge into main" : "Merge (owner only)"}
              </Button>
              <Button
                size="1"
                variant="soft"
                color="red"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
              >
                Delete
              </Button>
            </>
          )}
        </Flex>
      )}
    </Box>
  );
}

function InlineDiff({
  projectId,
  branchName,
}: {
  projectId: string;
  branchName: string;
}) {
  const isMain = branchName === "main";
  const { data: diff, isLoading } = useDiffBranches(
    projectId,
    branchName,
    "main",
    !isMain,
  );

  if (isMain) {
    return (
      <Text size="1" color="gray">
        Select a feature branch to see its diff against main.
      </Text>
    );
  }

  if (isLoading) {
    return <Text size="1" color="gray">Computing diff...</Text>;
  }

  const changes = diff?.filter((d) => d.status !== "unchanged") ?? [];

  if (changes.length === 0) {
    return (
      <Callout.Root color="gray" size="1">
        <Callout.Text>No differences from main.</Callout.Text>
      </Callout.Root>
    );
  }

  const added = changes.filter((c) => c.status === "only_b").length;
  const modified = changes.filter((c) => c.status === "modified").length;
  const deleted = changes.filter((c) => c.status === "only_a").length;

  return (
    <Flex direction="column" gap="2">
      <Flex gap="2">
        {added > 0 && <Badge size="1" color="green">{added} added</Badge>}
        {modified > 0 && <Badge size="1" color="yellow">{modified} modified</Badge>}
        {deleted > 0 && <Badge size="1" color="red">{deleted} deleted</Badge>}
      </Flex>

      <ScrollArea style={{ maxHeight: 200 }}>
        <Box asChild style={{ width: "100%", borderCollapse: "collapse" }}>
          <table>
            <thead>
              <tr>
                {["Path", "Status", "Branch", "Main"].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: "left",
                      padding: "4px 6px",
                      borderBottom: "1px solid var(--gray-a4)",
                    }}
                  >
                    <Text size="1" weight="medium">{h}</Text>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {changes.map((entry) => (
                <tr key={entry.path}>
                  <td style={{ padding: "4px 6px", borderBottom: "1px solid var(--gray-a3)" }}>
                    <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                      {entry.path}
                    </Text>
                  </td>
                  <td style={{ padding: "4px 6px", borderBottom: "1px solid var(--gray-a3)" }}>
                    <Badge
                      size="1"
                      color={
                        entry.status === "only_b" ? "green"
                        : entry.status === "modified" ? "yellow"
                        : entry.status === "only_a" ? "red"
                        : "gray"
                      }
                    >
                      {entry.status === "only_b" ? "added"
                        : entry.status === "only_a" ? "deleted"
                        : entry.status}
                    </Badge>
                  </td>
                  <td style={{ padding: "4px 6px", borderBottom: "1px solid var(--gray-a3)" }}>
                    <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                      {entry.value_a_json != null ? JSON.stringify(entry.value_a_json) : "—"}
                    </Text>
                  </td>
                  <td style={{ padding: "4px 6px", borderBottom: "1px solid var(--gray-a3)" }}>
                    <Text size="1" style={{ fontFamily: "var(--code-font-family, monospace)" }}>
                      {entry.value_b_json != null ? JSON.stringify(entry.value_b_json) : "—"}
                    </Text>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </ScrollArea>
    </Flex>
  );
}

interface Props {
  projectId: string;
}

export function BranchManagementPanel({ projectId }: Props) {
  const { currentBranch, setBranch } = useProvenanceContext();
  const { data: branches, isLoading } = useBranches(projectId);
  const deleteMutation = useDeleteBranch(projectId);
  const currentUserId = useCurrentUserId() ?? "";
  const { isOwner } = useCurrentRole(projectId);

  const [selectedBranch, setSelectedBranch] = useState<string>(currentBranch);
  const [newBranchOpen, setNewBranchOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeBranch, setMergeBranch] = useState("");
  const [renamingBranch, setRenamingBranch] = useState<string | null>(null);
  const [tagDialogOpen, setTagDialogOpen] = useState(false);
  const squashMutation = useSquashCommits(projectId);

  const handleDelete = useCallback(
    (name: string) => {
      if (!confirm(`Delete branch "${name}"? This cannot be undone.`)) return;
      deleteMutation.mutate(name);
      if (currentBranch === name) setBranch("main");
      if (selectedBranch === name) setSelectedBranch("main");
    },
    [currentBranch, deleteMutation, selectedBranch, setBranch],
  );

  const handleMerge = useCallback((name: string) => {
    setMergeBranch(name);
    setMergeOpen(true);
  }, []);

  const featureBranches = branches?.filter((b) => b.name !== "main") ?? [];
  const mainBranch = branches?.find((b) => b.name === "main");

  return (
    <Box style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box p="3" pb="2">
        <Flex align="center" justify="between">
          <Heading size="3">Branches</Heading>
          <Flex gap="2">
            <Button size="1" variant="soft" onClick={() => setTagDialogOpen(true)}>
              Tags
            </Button>
            <Button size="1" onClick={() => setNewBranchOpen(true)}>
              + New Branch
            </Button>
          </Flex>
        </Flex>
      </Box>

      {isLoading && (
        <Box p="3">
          <Text size="2" color="gray">Loading branches...</Text>
        </Box>
      )}

      <ScrollArea style={{ flex: 1 }}>
        <Flex direction="column" gap="2" p="3" pt="1">
          {mainBranch && (
            <BranchCard
              branch={mainBranch}
              projectId={projectId}
              isActive={selectedBranch === "main"}
              canMerge={isOwner}
              onSelect={() => setSelectedBranch("main")}
              onSwitch={() => setBranch("main")}
              onDelete={() => {}}
              onMerge={() => {}}
              onRename={() => {}}
            />
          )}

          {featureBranches.length > 0 && (
            <Text size="1" weight="medium" color="gray" mt="1">
              Feature branches ({featureBranches.length})
            </Text>
          )}

          {featureBranches.map((b) => (
            <BranchCard
              key={b.id}
              branch={b}
              projectId={projectId}
              isActive={selectedBranch === b.name}
              canMerge={isOwner}
              onSelect={() => setSelectedBranch(b.name)}
              onSwitch={() => setBranch(b.name)}
              onDelete={() => handleDelete(b.name)}
              onMerge={() => handleMerge(b.name)}
              onRename={() => setRenamingBranch(b.name)}
              onSquash={() => {
                const msg = prompt(`Squash all commits on "${b.name}" into one. Enter message:`);
                if (msg) squashMutation.mutate({ branch: b.name, message: msg });
              }}
            />
          ))}

          {!isLoading && featureBranches.length === 0 && (
            <Box
              p="4"
              style={{
                textAlign: "center",
                border: "1px dashed var(--gray-a5)",
                borderRadius: "var(--radius-2)",
              }}
            >
              <Text size="2" color="gray">
                No feature branches yet. Create one to start working in isolation.
              </Text>
            </Box>
          )}

          {/* Inline diff for selected feature branch */}
          {selectedBranch !== "main" && (
            <Box mt="2">
              <Text size="1" weight="medium" color="gray" mb="2" style={{ display: "block" }}>
                Changes in <Badge size="1" color="orange">{selectedBranch}</Badge> vs{" "}
                <Badge size="1" color="blue">main</Badge>
              </Text>
              <InlineDiff projectId={projectId} branchName={selectedBranch} />
            </Box>
          )}
        </Flex>
      </ScrollArea>

      <NewBranchDialog open={newBranchOpen} onOpenChange={setNewBranchOpen} />

      {mergeBranch && (
        <MergeDialog
          open={mergeOpen}
          onOpenChange={setMergeOpen}
          sourceBranch={mergeBranch}
          targetBranch="main"
          currentUserId={currentUserId}
          isProjectOwner={isOwner}
        />
      )}

      {renamingBranch && (
        <RenameBranchDialog
          open={!!renamingBranch}
          onOpenChange={(open) => { if (!open) setRenamingBranch(null); }}
          currentName={renamingBranch}
        />
      )}

      <TagDialog open={tagDialogOpen} onOpenChange={setTagDialogOpen} />
    </Box>
  );
}
